import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, addEdge,
  applyNodeChanges, applyEdgeChanges, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, Badge, DataTable, Loading, cx } from "./StudioShared";
import ActivityTimeline from "@/components/ActivityTimeline";
import { AiposNode } from "./workflow/AiposNode";
import NodeConfigPanel from "./workflow/NodeConfigPanel";
import RunReplayPanel from "./workflow/RunReplayPanel";
import { NODE_META, NODE_ORDER, BRANCHING } from "./workflow/nodeMeta";
import {
  Plus, Download, Upload, Rocket, Copy, Archive, ArchiveRestore, Trash2, ArrowLeft,
  Save, Play, Workflow as WorkflowIcon, PackageCheck, Layers,
} from "lucide-react";

const STATUS_TONE = { ready: "green", draft: "amber", archived: "red" };
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const shortId = (t) => `${t}_${Math.random().toString(36).slice(2, 8)}`;
const emptyStart = () => ([{ id: "start", type: "start", position: { x: 40, y: 200 }, data: { label: "Start", config: {} } }]);

/* Strip React Flow internals before persisting. */
const cleanNodes = (nodes) => nodes.map((n) => ({ id: n.id, type: n.type, position: n.position, data: { label: n.data?.label, config: n.data?.config || {} } }));
const cleanEdges = (edges) => edges.map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle || null, targetHandle: e.targetHandle || null, label: e.label || null, data: e.data || {} }));

export default function WorkflowBuilder() {
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(null); // {id?, name, purpose, department, category, status, is_template}
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  const openEditor = (wf) => {
    setEditing({ id: wf?.id, name: wf?.name || "Untitled Workflow", purpose: wf?.purpose || "",
      department: wf?.department || "", category: wf?.category || "", status: wf?.status || "draft", is_template: !!wf?.is_template });
    setNodes((wf?.nodes && wf.nodes.length ? wf.nodes : emptyStart()).map((n) => ({ ...n, data: { ...n.data, config: n.data?.config || {} } })));
    setEdges((wf?.edges || []).map((e) => ({ ...e, markerEnd: { type: MarkerType.ArrowClosed }, animated: true })));
    setMode("edit");
  };

  if (mode === "edit") {
    return (
      <ReactFlowProvider>
        <Editor editing={editing} setEditing={setEditing} nodes={nodes} setNodes={setNodes}
          edges={edges} setEdges={setEdges} onBack={() => setMode("list")} />
      </ReactFlowProvider>
    );
  }
  return <WorkflowList onOpen={openEditor} />;
}

/* --------------------------------------------------------------------- List */
function WorkflowList({ onOpen }) {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [department, setDepartment] = useState("");
  const [status, setStatus] = useState("");
  const [tmpl, setTmpl] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (department) p.set("department", department);
    if (status) p.set("status", status);
    if (tmpl === "templates") p.set("template", "true");
    if (tmpl === "workflows") p.set("template", "false");
    if (q) p.set("q", q);
    const [list, m] = await Promise.all([api.get(`/admin/workflows?${p}`), api.get("/admin/workflows/meta")]);
    setItems(list.data); setMeta(m.data);
  }, [department, status, tmpl, q]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const install = (w) => act(() => api.post(`/admin/workflows/${w.id}/clone?as_active=true`), "Template installed as a workflow.");
  const clone = (w) => act(() => api.post(`/admin/workflows/${w.id}/clone`), "Workflow cloned (draft).");
  const archive = (w) => act(() => api.post(`/admin/workflows/${w.id}/archive`), "Status updated.");
  const del = (w) => { if (window.confirm(`Delete "${w.name}"?`)) act(() => api.delete(`/admin/workflows/${w.id}`), "Workflow deleted."); };

  const exportAll = async () => {
    try { const { data } = await api.get("/admin/workflows/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "aipos-workflows.json"; a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${data.count} workflow(s).`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const parsed = JSON.parse(await file.text()); const list = Array.isArray(parsed) ? parsed : (parsed.workflows || []);
      const r = await api.post("/admin/workflows/import", { workflows: list }); toast.success(`Imported ${r.data.imported} workflow(s).`); load();
    } catch { toast.error("Import failed — expected JSON array or {workflows:[...]}."); } finally { e.target.value = ""; }
  };

  if (!items || !meta) return <Loading label="Loading workflows…" />;
  const c = meta.counts;
  const columns = [
    { key: "name", label: "Workflow", render: (r) => (
      <div className="min-w-0"><div className="text-white font-medium truncate flex items-center gap-2">{r.name}{r.is_template && <PackageCheck size={12} className="text-gold shrink-0" />}</div>
        <div className="text-slate-500 text-xs truncate">{r.purpose ? r.purpose.slice(0, 64) : r.id}</div></div>) },
    { key: "department", label: "Department", render: (r) => r.department || "—" },
    { key: "nodes", label: "Nodes", render: (r) => <span className="inline-flex items-center gap-1 text-slate-300"><Layers size={12} className="text-gold" />{r.node_count}</span> },
    { key: "type", label: "Type", render: (r) => <Badge tone={r.is_template ? "gold" : "slate"}>{r.is_template ? "template" : "workflow"}</Badge> },
    { key: "status", label: "Status", render: (r) => <Badge tone={STATUS_TONE[r.status] || "slate"}>{r.status}</Badge> },
    { key: "actions", label: "", render: (r) => (
      <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        {r.is_template && <IconBtn icon={Rocket} title="Install" testid={`wf-install-${r.id}`} onClick={() => install(r)} disabled={busy} />}
        <IconBtn icon={Copy} title="Clone" testid={`wf-clone-${r.id}`} onClick={() => clone(r)} disabled={busy} />
        <IconBtn icon={r.status === "archived" ? ArchiveRestore : Archive} title="Archive" testid={`wf-archive-${r.id}`} onClick={() => archive(r)} disabled={busy} />
        <IconBtn icon={Trash2} title="Delete" danger testid={`wf-del-${r.id}`} onClick={() => del(r)} disabled={busy} />
      </div>) },
  ];

  return (
    <StudioPage title="Workflow Builder" testid="workflow-builder-page"
      question="Design visual, executable AI workflows — drag nodes, connect them, run live, and replay every step."
      actions={
        <div className="flex flex-wrap gap-2">
          <label data-testid="wf-import-btn" className="cursor-pointer flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-2 text-sm hover:border-gold/50 hover:text-white transition-colors rounded-sm"><Upload size={15} /> Import<input type="file" accept="application/json" className="hidden" onChange={onImport} /></label>
          <button onClick={exportAll} data-testid="wf-export-btn" className="flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-2 text-sm hover:border-gold/50 hover:text-white transition-colors rounded-sm"><Download size={15} /> Export</button>
          <button onClick={() => onOpen(null)} data-testid="wf-new-btn" className="flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light transition-colors rounded-sm"><Plus size={15} /> New Workflow</button>
        </div>}>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Metric label="Total" value={c.total} testid="wf-count-total" />
        <Metric label="Ready" value={c.ready} testid="wf-count-ready" />
        <Metric label="Draft" value={c.draft} testid="wf-count-draft" />
        <Metric label="Templates" value={c.templates} testid="wf-count-templates" />
        <Metric label="Archived" value={c.archived} testid="wf-count-archived" />
      </div>

      <Panel className="p-3 mb-5 flex flex-wrap items-center gap-2" data-testid="wf-filters">
        <div className="flex gap-1 bg-navy border border-white/10 p-1 rounded-sm">
          {[["all", "All"], ["workflows", "Workflows"], ["templates", "Templates"]].map(([k, l]) => (
            <button key={k} onClick={() => setTmpl(k)} data-testid={`wf-type-filter-${k}`} className={cx("px-3 py-1.5 text-xs font-medium rounded-sm transition-colors", tmpl === k ? "bg-gold text-navy" : "text-slate-400 hover:text-white")}>{l}</button>
          ))}
        </div>
        <select value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="wf-department-filter" className="bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none">
          <option value="">All departments</option>{meta.departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="wf-status-filter" className="bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none">
          <option value="">Any status</option>{meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search workflows…" data-testid="wf-search" className="bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none flex-1 min-w-[160px]" />
      </Panel>

      <DataTable testid="wf-table" columns={columns} rows={items} onRowClick={onOpen} rowTestid={(r) => `wf-row-${r.id}`} empty="No workflows yet. Create one or install a flagship Template." />
    </StudioPage>
  );
}

const IconBtn = ({ icon: Icon, title, onClick, testid, danger, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid} title={title}
    className={cx("p-1.5 border rounded-sm transition-colors disabled:opacity-40", danger ? "border-red-500/30 text-red-300 hover:bg-red-500/10" : "border-white/10 text-slate-300 hover:border-gold/50 hover:text-white")}>
    <Icon size={13} />
  </button>
);

/* ------------------------------------------------------------------- Editor */
function Editor({ editing, setEditing, nodes, setNodes, edges, setEdges, onBack }) {
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("build");
  const [strategists, setStrategists] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [missions, setMissions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [run, setRun] = useState(null);
  const [inputs, setInputs] = useState({});
  const [replaying, setReplaying] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const addOffset = useRef(0);

  useEffect(() => {
    api.get("/admin/strategists").then((r) => setStrategists(r.data)).catch(() => {});
    api.get("/admin/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/admin/missions").then((r) => setMissions(r.data)).catch(() => {});
  }, []);

  const nodeTypes = useMemo(() => Object.fromEntries(NODE_ORDER.map((t) => [t, AiposNode])), []);
  const onNodesChange = useCallback((chs) => setNodes((nds) => applyNodeChanges(chs, nds)), [setNodes]);
  const onEdgesChange = useCallback((chs) => setEdges((eds) => applyEdgeChanges(chs, eds)), [setEdges]);
  const onConnect = useCallback((c) => setEdges((eds) => addEdge({ ...c, markerEnd: { type: MarkerType.ArrowClosed }, animated: true,
    data: c.sourceHandle ? { branch: c.sourceHandle } : {} }, eds)), [setEdges]);
  const onNodeClick = useCallback((_, n) => { setSelectedId(n.id); setTab("build"); }, []);

  const selected = nodes.find((n) => n.id === selectedId) || null;
  const updateNode = (updated) => setNodes((nds) => nds.map((n) => (n.id === updated.id ? updated : n)));
  const deleteNode = (id) => { setNodes((nds) => nds.filter((n) => n.id !== id)); setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id)); setSelectedId(null); };

  const addNode = (type) => {
    addOffset.current += 1;
    const id = type === "start" ? "start" : shortId(type);
    const meta = NODE_META[type];
    const pos = { x: 320 + (addOffset.current % 4) * 40, y: 120 + (addOffset.current % 6) * 60 };
    setNodes((nds) => [...nds, { id, type, position: pos, data: { label: meta.label, config: {} } }]);
    setSelectedId(id);
  };

  const doSave = async () => {
    if (!editing.name.trim()) { toast.error("Workflow name is required"); return; }
    setBusy(true);
    const payload = { name: editing.name, purpose: editing.purpose, department: editing.department,
      category: editing.category, status: editing.status, is_template: editing.is_template,
      nodes: cleanNodes(nodes), edges: cleanEdges(edges) };
    try {
      if (editing.id) { await api.patch(`/admin/workflows/${editing.id}`, payload); toast.success("Workflow saved (new version)."); }
      else { const r = await api.post("/admin/workflows", payload); setEditing((f) => ({ ...f, id: r.data.id })); toast.success("Workflow created."); }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const inputNodes = useMemo(() => nodes.filter((n) => n.type === "input"), [nodes]);
  useEffect(() => {
    setInputs((prev) => { const next = { ...prev }; inputNodes.forEach((n) => { const k = n.data?.config?.key; if (k && next[k] === undefined) next[k] = n.data?.config?.default ?? ""; }); return next; });
  }, [inputNodes]);

  const clearReplay = () => setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, replay: undefined } })));

  const doRun = async () => {
    if (!editing.id) { toast.error("Save the workflow before running."); return; }
    if (!nodes.some((n) => n.type === "start")) { toast.error("Add a Start node."); return; }
    setBusy(true); setTab("run"); clearReplay();
    try {
      const r = await api.post(`/admin/workflows/${editing.id}/run`, { inputs });
      setRun(r.data);
      toast.success(r.data.status === "waiting" ? "Paused — awaiting approval." : `Run ${r.data.status}.`);
      paintFinalStatuses(r.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doResume = async (decision, comment) => {
    if (!run) return;
    setBusy(true);
    try { const r = await api.post(`/admin/workflow-runs/${run.id}/resume`, { decision, comment });
      setRun(r.data); toast.success(`Run ${r.data.status}.`); paintFinalStatuses(r.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const paintFinalStatuses = (r) => {
    const byNode = {}; (r.steps || []).forEach((s) => { byNode[s.node_id] = s.status; });
    setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, replay: byNode[n.id] } })));
  };

  const playVisual = async () => {
    if (!run?.steps?.length) return;
    setReplaying(true); clearReplay();
    for (let i = 0; i < run.steps.length; i++) {
      const st = run.steps[i];
      setActiveIndex(i);
      setNodes((nds) => nds.map((n) => (n.id === st.node_id ? { ...n, data: { ...n.data, replay: "active" } } : n)));
      await sleep(650);
      setNodes((nds) => nds.map((n) => (n.id === st.node_id ? { ...n, data: { ...n.data, replay: st.status } } : n)));
    }
    setReplaying(false); setActiveIndex(-1);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] -m-5 md:-m-8" data-testid="wf-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10 bg-[#0A1128] flex-wrap">
        <button onClick={onBack} data-testid="wf-back-btn" className="flex items-center gap-1.5 text-slate-300 hover:text-white text-sm"><ArrowLeft size={16} /> Back</button>
        <div className="w-px h-5 bg-white/10" />
        <WorkflowIcon size={16} className="text-gold" />
        <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="wf-name-input"
          className="bg-transparent text-white font-medium text-sm border-b border-transparent focus:border-gold focus:outline-none min-w-[160px]" />
        <Badge tone={STATUS_TONE[editing.status] || "slate"}>{editing.status}</Badge>
        <div className="ml-auto flex items-center gap-2">
          <AddNodeMenu onAdd={addNode} />
          <button onClick={doSave} disabled={busy} data-testid="wf-save-btn" className="flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-1.5 text-sm rounded-sm hover:border-gold/50 hover:text-white disabled:opacity-60"><Save size={14} /> Save</button>
          <button onClick={doRun} disabled={busy} data-testid="wf-run-btn" className="flex items-center gap-1.5 bg-gold text-navy px-4 py-1.5 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-60"><Play size={14} /> Run</button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Canvas */}
        <div className="flex-1 min-w-0 bg-[#070E20]" data-testid="wf-canvas">
          <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
            onNodeClick={onNodeClick} onPaneClick={() => setSelectedId(null)}
            fitView proOptions={{ hideAttribution: true }} defaultEdgeOptions={{ animated: true }}>
            <Background color="#1e293b" gap={20} />
            <Controls className="!bg-navy-800 !border-white/10" />
            <MiniMap pannable zoomable className="!bg-navy-800 !border !border-white/10" maskColor="rgba(7,14,32,0.7)"
              nodeColor={(n) => (NODE_META[n.type]?.group === "Intelligence" ? "#D4AF37" : "#334155")} />
          </ReactFlow>
        </div>

        {/* Right sidebar */}
        <div className="w-[360px] shrink-0 border-l border-white/10 bg-[#0A1128] flex flex-col" data-testid="wf-sidebar">
          <div className="flex gap-1 p-2 border-b border-white/10">
            {[["build", "Build"], ["run", "Run & Replay"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`wf-side-tab-${k}`}
                className={cx("flex-1 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors", tab === k ? "bg-gold text-navy" : "text-slate-400 hover:text-white")}>{l}</button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {tab === "build" ? (
              selected ? (
                <NodeConfigPanel node={selected} strategists={strategists} departments={departments} missions={missions}
                  onChange={updateNode} onDelete={deleteNode} />
              ) : (
                <WorkflowSettings editing={editing} setEditing={setEditing} departments={departments} nodeCount={nodes.length} />
              )
            ) : (
              <div className="space-y-4">
                {inputNodes.length > 0 && (
                  <div>
                    <SectionLabel className="mb-2">Run inputs</SectionLabel>
                    <div className="space-y-2" data-testid="wf-run-inputs">
                      {inputNodes.map((n) => { const k = n.data?.config?.key; if (!k) return null; return (
                        <div key={n.id}>
                          <label className="text-[11px] text-slate-500">{k}</label>
                          <input value={inputs[k] ?? ""} onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })} data-testid={`wf-input-${k}`}
                            className="w-full bg-navy border border-white/10 px-3 py-1.5 text-sm text-white rounded-sm focus:border-gold focus:outline-none" />
                        </div>); })}
                    </div>
                  </div>
                )}
                <RunReplayPanel run={run} busy={busy} replaying={replaying} activeIndex={activeIndex}
                  onPlayVisual={playVisual} onResume={doResume} />
                {editing?.id && (
                  <div className="border-t border-white/10 pt-3">
                    <ActivityTimeline title="Execution Timeline" mode="entity" params={{ workflow: editing.id }} limit={25}
                      testid="wf-activity-timeline" emptyText="No executions recorded yet." />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkflowSettings({ editing, setEditing, departments, nodeCount }) {
  const inp = "w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none";
  return (
    <div className="space-y-3" data-testid="wf-settings">
      <p className="text-xs text-slate-500">Select a node to configure it, or edit workflow settings below. {nodeCount} node(s).</p>
      <div><SectionLabel className="mb-1">Purpose</SectionLabel><textarea rows={3} value={editing.purpose} onChange={(e) => setEditing({ ...editing, purpose: e.target.value })} className={inp} data-testid="wf-purpose" /></div>
      <div><SectionLabel className="mb-1">Department</SectionLabel>
        <select value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })} className={inp} data-testid="wf-dept">
          <option value="">— none —</option>{departments.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
        </select>
      </div>
      <div><SectionLabel className="mb-1">Status</SectionLabel>
        <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inp} data-testid="wf-status-select">
          <option value="draft">draft</option><option value="ready">ready</option><option value="archived">archived</option>
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={editing.is_template} onChange={(e) => setEditing({ ...editing, is_template: e.target.checked })} className="accent-gold" data-testid="wf-template-toggle" /> Save as installable Template</label>
    </div>
  );
}

function AddNodeMenu({ onAdd }) {
  const [open, setOpen] = useState(false);
  const groups = useMemo(() => {
    const g = {};
    NODE_ORDER.forEach((t) => { const meta = NODE_META[t]; (g[meta.group] = g[meta.group] || []).push({ type: t, ...meta }); });
    return g;
  }, []);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="wf-add-node-btn" className="flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-1.5 text-sm rounded-sm hover:bg-gold/10"><Plus size={14} /> Add Node</button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-10 z-50 w-60 bg-[#0A1128] border border-white/10 rounded-sm shadow-2xl p-2 max-h-[70vh] overflow-y-auto" data-testid="wf-add-node-menu">
            {Object.entries(groups).map(([grp, items]) => (
              <div key={grp} className="mb-1">
                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500">{grp}</div>
                {items.map((it) => (
                  <button key={it.type} onClick={() => { onAdd(it.type); setOpen(false); }} data-testid={`wf-add-${it.type}`}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-slate-300 hover:bg-white/5 rounded-sm">
                    <it.icon size={14} className={it.accent} /> {it.label}{BRANCHING.has(it.type) && <span className="ml-auto text-[9px] text-slate-500">T/F</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
